import "react-use-audio-recorder/dist/index.css";

import { Badge, Box, Button, Container, Flex, HStack, IconButton } from "@chakra-ui/react";
import React, { MutableRefObject, useRef, useState } from "react";
import { IoMic, IoStopCircle } from "react-icons/io5";
import { useReactMediaRecorder } from "react-media-recorder";
import { useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";

import { postAnswer, updateTimeLeft } from "../../shared/Api";
import AudioRecorder, {AudioRecorderStart}  from "../../shared/components/AudioRecorder";
import Markdown from "../../shared/components/Markdown";
import RenderResource, {ResourceUpdate} from "../../shared/components/RenderResource";
import RenderText from "../../shared/components/RenderText";
import TimerComponent, {TimerComponentStop} from "../../shared/components/TimerComponent";
import {useFont} from "../../shared/hooks/FontProvider";
import {useTheme} from "../../shared/hooks/ThemeProvider";
import { CandidateStatus , customButtonProps, QuestionParameterKey, TestType } from "../../shared/model/Constants";
import { StatusDto, TestDto } from "../../shared/model/Dto";
import { publicKeys } from "../../shared/QueryKeys";
import { getUniqueOrigin, isInAvataiSite, validResourceExtension } from "../../shared/Utils";
import Options from "./Options";

type QuestionProps = {
  test: TestDto;
  status: StatusDto;
  isLoading: boolean;
  preparationTime?: number;
  responseTime?: number;
  onNextQuestion: (timeLeft: number) => void;
  onReRender?: () => void;
  timeLeft?: MutableRefObject<number>;
};

/*

 step 1 (optional): if question has ADDITIONAL_RESOURCE, play audio
 step 2: start preparation timer. can finish early with start recording button
 step 3: start recording (response time included audoRecorder timer)
    3.5: what if recorder audio is invalid?
 step 4: await click on continue button

 what if user leaves site during each step?
 */
// Rerendering this component does not rerender the entire component.
// audio elements on other components don't stop when this is rerendered
const AnswerSection = (props: QuestionProps) => {
  const theme = useTheme().theme;
  const font = useFont().font;
  const isAvatai = isInAvataiSite();
  const preparationTimer = useRef<number>(props.preparationTime || 0);
  const audioRecorderRef = useRef<AudioRecorderStart>(null);
  const timerComponentRef = useRef<TimerComponentStop>(null);
  const resourceRef = useRef<ResourceUpdate>(null);

  const params = useParams<{
    publicId: string;
    candidateToken: string;
  }>();
  const candidateToken = params.candidateToken || "";

  const preparationOverAudioRef = useRef<ResourceUpdate>(null);
  const audioSavedAudioRef = useRef<ResourceUpdate>(null);

  const [recordedAudio, setRecordedAudio] = useState<Blob | undefined>(undefined);
  const [isSilent, setIsSilent] = useState<boolean>(false);
  const [recorderButtonDisabled, setRecorderButtonDisabled] = useState<boolean>(true);
  const [preparing, setPreparing] = useState<boolean>(true);

  const endPreparation = () =>{
    setPreparing(false);
    if (timerComponentRef.current) timerComponentRef.current.stopTimer();
    //if (audioRecorderRef.current) audioRecorderRef.current.startRecording();
    if (resourceRef.current) resourceRef.current.stopPlayer();
    if (preparationOverAudioRef.current) preparationOverAudioRef.current.startPlayer();
  }

  /*
   TODO: use map of answerProperties to handle optionIds, answerBlob, isValidBlob...
   */
  const postAnswerMutation = useMutation(
    ({ publicId, question, skillType, timeLeft, answerBlob, isValidBlob }: any) =>
      postAnswer(publicId, question, [], timeLeft, answerBlob, isValidBlob, candidateToken, skillType),
    {
      onSuccess: () => {
        if (audioSavedAudioRef.current) audioSavedAudioRef.current.startPlayer();
      }
    }
  );
  function handleClick() {
    if (props.timeLeft && recordedAudio) {
        //props.onNextQuestion(props.timeLeft.current, recordedAudio, !isSilent);
        props.onNextQuestion(props.timeLeft.current);
    }
    if (props.onReRender) props.onReRender();
    /*
                onClick={() => {
                    if (props.timesPlayed && props.timeLeft) {
                        props.test.nextQuestion.timesPlayed = props.timesPlayed.current;
                        props.onNextQuestion(optionIds, props.timeLeft.current);
                        // props.onNextQuestion(optionIds, props.timesPlayed);
                        setOptionIds([]);
                        props.timesPlayed.current = 0;
                        // props.timesPlayed.current = props.test.nextQuestion.timesPlayed;
                    }
                    if (props.onReRender) props.onReRender();
                }} */
  }

  const hasInitialAudio = QuestionParameterKey.ADDITIONAL_RESOURCE in props.test.nextQuestion.parameters &&
           validResourceExtension(props.test.nextQuestion.parameters[QuestionParameterKey.ADDITIONAL_RESOURCE]);
  const [startTimerOnChange, setStartTimerOnChange] = useState<boolean>(!hasInitialAudio);

  return (
        <>
        { hasInitialAudio &&
          <Box textAlign="right" marginBottom={"1em"}>
            <RenderResource ref={resourceRef}
                resource={props.test.nextQuestion.parameters[QuestionParameterKey.ADDITIONAL_RESOURCE]}
                subdirectory={"Tests/"+ (getUniqueOrigin(props.test.nextQuestion)===''  ?
                    props.test.publicId : getUniqueOrigin(props.test.nextQuestion))}
                maxNumberOfReproductions={1}
                autoPlay={true}
                hidePlayButton={true}

                onAudioEnded={() => {
                    setStartTimerOnChange(true);
                  }
                }
            />
          </Box>
        }
        <HStack>
            <TimerComponent timeLeft={preparationTimer}  ref={timerComponentRef}
                            onTimerFinished={(timeLeft: number) => {
                                endPreparation();
                            }}
                            startOnChange={startTimerOnChange}
                        displayText="Preparation Time: "
            />

            <Button
              colorScheme={theme.COLOR_SCHEME}
              disabled={!preparing}

              onClick={() => {  endPreparation();   }}
            >
              Start recording
            </Button>

            <RenderResource  ref={preparationOverAudioRef}
              resource="PreparationTimeOver.mp3"
              hidden={true}
              onAudioEnded={() => {
                  if (audioRecorderRef.current) audioRecorderRef.current.startRecording();
                  setRecorderButtonDisabled(false);
                }
              }
            />
        </HStack>

        <AudioRecorder demo={false}  ref={audioRecorderRef}
          responseTime={props.responseTime}
          disabled={recorderButtonDisabled}
          onRecordingEnded={(isSilent: boolean, blob: Blob) => {
               setRecorderButtonDisabled(true);
               setIsSilent(isSilent);
               setRecordedAudio(blob);
               postAnswerMutation.mutate({
                     publicId: props.test.publicId,
                     question: props.test.nextQuestion,
                     skillType: props.test.skillType,
                     timeLeft: props.timeLeft?.current,
                     answerBlob: blob,
                     isValidBlob: !isSilent
                   });

              }
          }
        />

        <RenderResource  ref={audioSavedAudioRef}
          resource="ResponseSaved.mp3"
            hidden={true}
        />

        <Box textAlign="right">
            <Button
                onClick={handleClick}
                isLoading={props.isLoading}
                disabled={recordedAudio===undefined}
                {...customButtonProps(isAvatai)}
                fontWeight={font.weight}
                fontSize={font.buttonText}
            >
                Continue
            </Button>
        </Box>
  </>)
}

const RecordingQuestion = (props: QuestionProps) => {
  const theme = useTheme().theme;
  const timeLeft = useRef<number>(props.test.timeLeft);
  const [reRender, setReRender] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const params = useParams<{
    publicId: string;
    candidateToken: string;
  }>();
  const publicId = params.publicId || "";
  const candidateToken = params.candidateToken || "";

  // For questions that have text with bold and non-bold sections
  let parsedText=
    props.test.nextQuestion.statement ? (props.test.nextQuestion.statement.startsWith('**') ?
      `${props.test.nextQuestion.statement.slice(2)}` :
      `**${props.test.nextQuestion.statement}` ) : "";

  parsedText = parsedText.endsWith('**') ?
    parsedText.slice(0,parsedText.length-2) :
    parsedText+'**';

  const postUpdateTimeLeftMutation = useMutation(
    ({ timeLeft }: any) =>
      updateTimeLeft(publicId, candidateToken, timeLeft),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(publicKeys.testsByPublicId(publicId)),
    }
  );


  const handleReRender = () => {
    setReRender(!reRender);
  };

    return (
      <Container paddingInline="5" maxW="max" aria-hidden="true">
        {/* props.test.timeLimit !== 0 &&
        <TimerComponent timeLeft={timeLeft}
                        onTimerFinished={(timeLeft: number) => {
                          // only ends test if it is single adaptive, not multistage
                          //if (props.test.testType === TestType.ADAPTIVE &&
                          //  (props.test.conditions && props.test.conditions.includes("ADAPTIVE"))
                          //)
                            postUpdateTimeLeftMutation.mutate({ timeLeft });
                          }
                        }
                        />
        */}
        <Flex justify="center" align="space-between" direction="column">
            <Box width="2xl"
                padding="2"
                borderWidth="1px"
                borderRadius="lg"
                borderColor={theme.OUTLINE}
                borderStyle="solid"
                >
                <HStack><Badge alignSelf="start">#{props.test.status.answered + 1}</Badge>
                <Box width="xl">
                    { props.test.nextQuestion.instructions != null &&
                      <RenderText id={props.test.nextQuestion.instructions}
                                  content={`**${props.test.nextQuestion.instructions}**`}
                                   />
                    }
                </Box>
                </HStack>
                { validResourceExtension(props.test.nextQuestion.body)
                    ? <RenderResource
                        resource={props.test.nextQuestion.body}
                        subdirectory={"Tests/"+props.test.publicId}
                        conditions={props.test.conditions}
                        small={true}
                    />
                    : <Markdown content={props.test.nextQuestion.body} />
                }

                { props.test.nextQuestion.statement != null &&
                    <RenderText id={props.test.nextQuestion.statement} debug={true}
                                content={parsedText} />
                }
            </Box>
            <Box width="2xl"
                 padding="2"
                 marginBlock="2"
                 borderWidth="1px"
                 borderRadius="lg"
                 borderColor={theme.OUTLINE}
                 borderStyle="solid"
                 >
              <AnswerSection  isLoading={props.isLoading}
                               onNextQuestion={props.onNextQuestion}
                               status={props.status}
                               test={props.test}
                               onReRender={handleReRender}
                               timeLeft={timeLeft}
                               preparationTime={props.preparationTime}
                               responseTime={props.responseTime}
              />
            </Box>
        </Flex>
      </Container>
    );
};

export default RecordingQuestion;